import React, { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Grid, TextField, Switch, FormControlLabel, Typography, Autocomplete } from "@mui/material"

import RegularButton from "components/CustomButtons/Button"

import { orderArrayV2 } from "helpers/orderArray"

import { useForm } from "hooks/useForm"
import { useSearch } from "hooks/useSearch"

import {
    setStationBrands,
    createPromocode,
    editPromocode
} from "redux/actions/promocodes/promocodesActions"

import { initialValues } from "./initialValues"

const KILOS_UNITS = ["CILINDRO", "KGS"]
const LITERS_UNITS = ["LTS"]

const getAllowedDiscountUnit = (product) => {
    if (!product) return null
    const measureUnit = (product.measureUnit || "").toUpperCase()
    const name = (product.name || product.label || "").toUpperCase()

    if (KILOS_UNITS.includes(measureUnit) || name.startsWith("CILINDRO") || name === "KILOS") {
        return "KGS"
    }
    if (LITERS_UNITS.includes(measureUnit) || name === "PICTELEO" || name === "LITROS") {
        return "LTS"
    }
    return null
}

export const PromocodesForm = ({
    onClose,
    station = {}
}) => {
    const dispatch = useDispatch()
    const { stationBrands, discountTypes, useTypes, addMode, promocodeSelected } = useSelector(({ promocodes }) => promocodes)
    const { setSnackbar } = useSearch()
    const { brands } = useSelector((state) => state.brandsReducer);
    const { products } = useSelector(({ routesStatus }) => routesStatus)
    const { stationsList } = useSelector(({ companies }) => companies)
    const { isLoadingComp } = useSelector(({ search }) => search)
    const {
        inputValues,
        fieldErrors,
        handleInputOnChange,
        handleNewValues,
        runValidates
    } = useForm(initialValues.values, initialValues.required)

    const {
        idStation,
        idBrand,
        brandName,
        code,
        productType,
        discountType,
        discount,
        minPurchase,
        startDate,
        endDate,
        purchaseNumber,
        usesPerCustomer,
        status,
        redemptionLimit
    } = inputValues

    const filteredDiscountTypes = useMemo(() => {
        const allowedUnit = getAllowedDiscountUnit(productType)
        if (!allowedUnit) return discountTypes || []
        const excludedUnit = allowedUnit === "KGS" ? "LTS" : "KGS"
        return (discountTypes || []).filter(opt => opt.value !== excludedUnit)
    }, [productType, discountTypes])

    useEffect(() => {
        if (!productType || !discountType) return
        const stillValid = filteredDiscountTypes.some(opt => opt.value === discountType.value)
        if (!stillValid) {
            handleNewValues({ discountType: null })
        }
    }, [productType])

    useEffect(() => {
        if (!promocodeSelected.rules?.length) return
        
        const stationFound = stationsList.find(obj => obj.value === promocodeSelected.idStation)
        if (!stationFound) return
        
        // Primero establecer la estación para cargar las marcas
        if (!idStation || idStation.value !== stationFound.value) {
            handleNewValues({ idStation: stationFound })
            return
        }
        
        // Luego cargar el resto cuando ya tengamos las marcas
        if (stationBrands.length > 0) {
            const brandFound = stationBrands.find(obj => 
                obj.value === promocodeSelected.rules[0].brand.id || 
                obj.idBrand === promocodeSelected.rules[0].brand.id ||
                obj.id === promocodeSelected.rules[0].brand.id
            )
            
            handleNewValues({
                purchaseNumber: promocodeSelected.rules[0].nBuyNumber,
                brandName: brandFound?.name || promocodeSelected.rules[0].brand.name,
                startDate: promocodeSelected.initialValidity?.substr(0, 10),
                endDate: promocodeSelected.finalValidity?.substr(0, 10),
                idBrand: brandFound,
                productType: products.find(obj => obj.value === promocodeSelected.rules[0].idProduct),
                discountType: discountTypes.find(obj => obj.value === promocodeSelected.rules[0].unit),
                usesPerCustomer: useTypes.find(obj => obj.value === promocodeSelected.rules[0].ruleType),
                discount: promocodeSelected.rules[0].discount,
                minPurchase: promocodeSelected.rules[0].minimum,
                redemptionLimit: promocodeSelected.rules[0].redemptionLimit,
                status: promocodeSelected.status,
                code: promocodeSelected.code
            })
        }
    }, [promocodeSelected, stationBrands, idStation])

    useEffect(() => {
        handleNewValues({ idStation: station })
    }, [])

    useEffect(() => {
        if (brands && idStation && idStation.value) {
            const uniqueBrands = Object.values(brands)
                .filter(brand => brand.idStation === idStation.value)
                .reduce((acc, brand) => {
                    if (!acc.find(b => b.idBrand === brand.idBrand)) {
                        acc.push({ ...brand, value: brand.idBrand, label: brand.name });
                    }
                    return acc;
                }, [])
                .sort((a, b) => a.name.localeCompare(b.name));
            
            dispatch(setStationBrands(uniqueBrands))
        }
    }, [brands, idStation]);

    const hasChanges = useMemo(() => {
        if (addMode) return true
        if (!promocodeSelected?.rules?.length) return false
        
        // Si aún no se han cargado los datos iniciales, no hay cambios
        if (!code || !startDate || !endDate || !discountType) return false
        
        // Si la marca es requerida pero no está seleccionada, no permitir guardar
        if (!idBrand) return false
        
        const changes = {
            code: code !== promocodeSelected.code,
            startDate: startDate !== promocodeSelected.initialValidity?.substr(0, 10),
            endDate: endDate !== promocodeSelected.finalValidity?.substr(0, 10),
            discount: parseFloat(discount) !== promocodeSelected.rules[0].discount,
            discountType: discountType?.value !== promocodeSelected.rules[0].unit,
            usesPerCustomer: (usesPerCustomer?.value || null) !== (promocodeSelected.rules[0].ruleType || null),
            purchaseNumber: parseInt(purchaseNumber || 0) !== (promocodeSelected.rules[0].nBuyNumber || 0),
            productType: (productType?.value || null) !== (promocodeSelected.rules[0].idProduct || null),
            minPurchase: parseFloat(minPurchase || 0) !== promocodeSelected.rules[0].minimum,
            redemptionLimit: parseInt(redemptionLimit || 0) !== promocodeSelected.rules[0].redemptionLimit,
            status: status !== promocodeSelected.status,
            idStation: idStation?.value !== promocodeSelected.idStation,
            idBrand: (idBrand?.value || idBrand?.idBrand || null) !== (promocodeSelected.rules[0].brand.id || null)
        }
        
        return Object.values(changes).some(changed => changed)
    }, [addMode, promocodeSelected, code, startDate, endDate, discount, discountType, usesPerCustomer, purchaseNumber, productType, minPurchase, redemptionLimit, status, idStation, idBrand])

    const handleNumberInput = (e, maxValue, maxLength) => {
        const value = e.target.value
        if (value && (parseFloat(value) > maxValue || value.length > maxLength)) {
            return
        }
        handleInputOnChange(e)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (runValidates()) return

        // Validaciones adicionales
        const start = new Date(startDate + 'T00:00:00')
        const end = new Date(endDate + 'T00:00:00')

        if (end < start) {
            return setSnackbar("La fecha de expiración no puede ser anterior a la fecha de inicio", "danger")
        }

        if (usesPerCustomer?.value === 'n_buy' && (!purchaseNumber || purchaseNumber <= 0)) {
            return setSnackbar("Compra en la que se aplicará el cupón obligatorio", "danger")
        }

        if (usesPerCustomer?.value === 'n_buy' && purchaseNumber > 9999) {
            return setSnackbar("Compra en la que se aplicará el cupón no puede ser mayor a 9999", "danger")
        }

        if (discountType?.value === 'PORCENTAJE' && parseFloat(discount) > 100) {
            return setSnackbar("El descuento no puede ser mayor al 100%", "danger")
        }

        if (parseFloat(discount) > 999999.99) {
            return setSnackbar("El descuento no puede ser mayor a 999,999.99", "danger")
        }

        if (minPurchase && parseFloat(minPurchase) > 999999.99) {
            return setSnackbar("El mínimo de compra no puede ser mayor a 999,999.99", "danger")
        }

        if (redemptionLimit && parseInt(redemptionLimit) > 999999) {
            return setSnackbar("El máximo de usos no puede ser mayor a 999,999", "danger")
        }

        if (discountType?.value === 'IMPORTE' && minPurchase > 0 && parseFloat(discount) > parseFloat(minPurchase)) {
            return setSnackbar("El descuento no puede ser mayor al mínimo de compra", "danger")
        }

        const form = {
            code,
            initialValidity: startDate,
            finalValidity: endDate,
            discount: parseFloat(discount),
            unit: discountType.value,
            ruleType: usesPerCustomer?.value || null,
            nBuyNumber: usesPerCustomer?.value === 'n_buy' && purchaseNumber ? parseInt(purchaseNumber) : null,
            idProduct: productType?.value || null,
            minimum: minPurchase ? parseFloat(minPurchase) : 0,
            redemptionLimit: redemptionLimit ? parseInt(redemptionLimit) : null,
            idStation: idStation.value,
            idBrand: idBrand.value,
            status: typeof status === 'string' ? status : (status?.value || 'ACTIVE')
        }

        if (promocodeSelected?.id) {
            form.id = promocodeSelected.id
            dispatch(editPromocode(form, onClose))
        } else {
            dispatch(createPromocode(form, onClose))
        }
    }

    return (
        <Grid container spacing={1} style={{ transition: "ease" }}>
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={status?.value === 'ACTIVE' || status === 'ACTIVE'}
                            onChange={(e) => handleNewValues({ 
                                status: e.target.checked ? 'ACTIVE' : 'INACTIVE'
                            })}
                            color="primary"
                        />
                    }
                    label={(status?.value === 'ACTIVE' || status === 'ACTIVE') ? 'Activo' : 'Inactivo'}
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="promocode-station-options"
                    options={orderArrayV2(stationsList || [])}
                    value={idStation}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Planta"
                            variant="outlined"
                            error={fieldErrors.idStation}
                            helperText={fieldErrors.idStation || ""}
                        />
                    )}
                    fullWidth
                    onChange={(e, v) => {
                        handleNewValues({ idStation: v, idBrand: null, brandName: '' })
                    }}
                    noOptionsText="No se encuentra la Planta"
                    disabled={(station && station.value) || (!addMode && !!idStation)}
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="promocode-brand-options"
                    options={orderArrayV2(stationBrands || [])}
                    value={idBrand}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Marca *"
                            variant="outlined"
                            error={fieldErrors.idBrand}
                            helperText={fieldErrors.idBrand || ""}
                        />
                    )}
                    fullWidth
                    onChange={(e, v) => handleNewValues({ idBrand: v, brandName: v?.name || '' })}
                    noOptionsText="No se encuentra la marca"
                    disabled={!addMode && !!idBrand}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Razón Social"
                    name="brandName"
                    value={brandName}
                    variant="outlined"
                    disabled
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    fullWidth
                    label="Código"
                    name="code"
                    value={code}
                    variant="outlined"
                    onChange={handleInputOnChange}
                    inputProps={{
                        maxLength: 10
                    }}
                    error={fieldErrors.code}
                    helperText={fieldErrors.code || ""}
                />
            </Grid>
            <Grid item xs={6}>
                <Autocomplete
                    id="promocode-product-type-options"
                    options={orderArrayV2(products || [])}
                    value={productType}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Tipo de producto"
                            variant="outlined"
                            error={fieldErrors.productType}
                            helperText={fieldErrors.productType || ""}
                        />
                    )}
                    fullWidth
                    onChange={(e, v) => handleNewValues({ productType: v })}
                    noOptionsText="No se encuentro el tipo de producto"
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="promocode-discount-type-options"
                    options={orderArrayV2(filteredDiscountTypes)}
                    value={discountType}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Tipo de descuento *"
                            variant="outlined"
                            error={fieldErrors.discountType}
                            helperText={fieldErrors.discountType || ""}
                        />
                    )}
                    fullWidth
                    onChange={(e, v) => handleNewValues({ discountType: v })}
                    noOptionsText="No se encuentro el tipo de descuento"
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    fullWidth
                    label="Descuento *"
                    type="number"
                    name="discount"
                    value={discount}
                    variant="outlined"
                    onChange={(e) => handleNumberInput(e, 999999.99, 10)}
                    error={fieldErrors.discount}
                    helperText={fieldErrors.discount || ""}
                    inputProps={{ min: 0, step: "0.01" }}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    fullWidth
                    label="Mínimo de compra"
                    type="number"
                    name="minPurchase"
                    value={minPurchase}
                    variant="outlined"
                    onChange={(e) => handleNumberInput(e, 999999.99, 10)}
                    error={fieldErrors.minPurchase}
                    helperText={fieldErrors.minPurchase || ""}
                    inputProps={{ min: 0, step: "0.01" }}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Fecha de inicio *"
                    type="date"
                    name="startDate"
                    value={startDate}
                    onChange={handleInputOnChange}
                    variant="outlined"
                    fullWidth
                    error={fieldErrors.startDate}
                    helperText={fieldErrors.startDate || ""}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Fecha de expiración *"
                    type="date"
                    name="endDate"
                    value={endDate}
                    onChange={handleInputOnChange}
                    variant="outlined"
                    fullWidth
                    error={fieldErrors.endDate}
                    helperText={fieldErrors.endDate || ""}
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="promocode-customer-uses-options"
                    options={orderArrayV2(useTypes || [])}
                    value={usesPerCustomer}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Tipo de Regla"
                            variant="outlined"
                            error={fieldErrors.usesPerCustomer}
                            helperText={fieldErrors.usesPerCustomer || ""}
                        />
                    )}
                    fullWidth
                    onChange={(e, v) => handleNewValues({ usesPerCustomer: v, purchaseNumber: v?.value === 'n_buy' ? purchaseNumber : null })}
                />
            </Grid>
            {usesPerCustomer?.value === 'n_buy' && <>
                <Grid item xs={12}>
                    <TextField
                        label="Compra en la que se aplicará el cupón *"
                        type="number"
                        name="purchaseNumber"
                        value={purchaseNumber || ''}
                        onChange={(e) => handleNumberInput(e, 9999, 4)}
                        variant="outlined"
                        fullWidth
                        error={fieldErrors.purchaseNumber}
                        helperText={fieldErrors.purchaseNumber || ""}
                        inputProps={{ min: 1, step: 1 }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                        El cupón solo podrá utilizarse en esa compra exacta. Solo se consideran pedidos finalizados.
                    </Typography>
                </Grid>
            </>}
            <Grid item xs={12}>
                <TextField
                    label="Máximo de usos"
                    type="number"
                    name="redemptionLimit"
                    value={redemptionLimit || ''}
                    onChange={(e) => handleNumberInput(e, 999999, 6)}
                    variant="outlined"
                    fullWidth
                    error={fieldErrors.redemptionLimit}
                    helperText={fieldErrors.redemptionLimit || ""}
                    inputProps={{ min: 0, step: 1 }}
                />
            </Grid>
            <Grid
                item
                xs={12}
                style={{
                    marginTop: 30,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                }}
            >
                <RegularButton
                    color="danger"
                    onClick={onClose}
                    disabled={isLoadingComp?.loadingCreatepromocode}
                >
                    Cancelar
                </RegularButton>
                <RegularButton
                    color="info"
                    onClick={handleSubmit}
                    loading={isLoadingComp?.loadingCreatepromocode}
                    disabled={!addMode && !hasChanges}
                >
                    {addMode ? 'Crear cupón' : 'Guardar'}
                </RegularButton>
            </Grid>
        </Grid>
    )
}
